import {Component, EventEmitter, inject, Input, OnInit, Output, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    AlternativeNameDto,
    AuthService,
    ContactInfoDto,
    EmployeesClient,
    PermissionType,
    UpdateAlternativeNameCommand,
    UpdateContactInfoCommand
} from '@services';
import {ScreenService} from 'src/app/services';
import notify from 'devextreme/ui/notify';
import {
    DxButtonModule,
    DxTextBoxModule,
    DxValidatorModule,
    DxValidationGroupModule,
    DxValidationGroupComponent,
    DxValidatorComponent
} from 'devextreme-angular';
import {FormPhotoComponent} from 'src/app/components';
import {Observable, throwError, of, forkJoin} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DxiValidationRuleComponent} from "devextreme-angular/ui/nested";

@Component({
    selector: 'app-contact-info',
    templateUrl: './contact-info.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxButtonModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxValidationGroupModule,
        FormPhotoComponent,
        DxiValidationRuleComponent
    ]
})
export class ContactInfoComponent implements OnInit {
    protected screen = inject(ScreenService);
    private employeesClient = inject(EmployeesClient);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();
    @Output() changePasswordRequested = new EventEmitter<void>();

    @ViewChild('validationGroup', {static: false}) validationGroup: DxValidationGroupComponent;
    @ViewChildren('alternativeNameValidator') alternativeNameValidators: QueryList<DxValidatorComponent>;

    contactData: ContactInfoDto;
    alternativeName: AlternativeNameDto | null = null;
    isLoading = false;

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getContactInfo(this.userId).subscribe({
            next: (data) => {
                this.contactData = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load contact info', 'error', 3000);
            }
        });

        if (this.canViewAlternativeName) {
            this.employeesClient.getAlternativeName(this.userId).subscribe({
                next: (data) => {
                    this.alternativeName = data;
                },
                error: () => {
                    notify('Failed to load alternative name', 'error', 3000);
                }
            });
        }
    }

    public save(): Observable<void> {
        const editableAlternativeName = this.isAlternativeNameReadOnly ? null : this.alternativeName;
        if (this.isReadOnly && !editableAlternativeName) return of(void 0);
        if (!this.validationGroup) return of();

        // Read-only contact fields keep their validators, so they must not block an alternative-name-only save.
        const isValid = this.isReadOnly
            ? this.alternativeNameValidators.map(v => v.instance.validate().isValid).every(Boolean)
            : this.validationGroup.instance.validate().isValid;
        if (!isValid) {
            notify({message: 'Please fix contact info errors', position: 'bottom center'}, 'error', 3000);
            return throwError(() => new Error('Validation failed'));
        }

        const saveContactInfo$ = this.isReadOnly
            ? of(void 0)
            : this.employeesClient.updateContactInfo(new UpdateContactInfoCommand({
                userId: this.userId,
                firstName: this.contactData.firstName,
                middleName: this.contactData.middleName,
                lastName: this.contactData.lastName,
                address: this.contactData.address,
                email: this.contactData.email,
                phone: this.contactData.phone
            }));

        const saveAlternativeName$ = editableAlternativeName
            ? this.employeesClient.updateAlternativeName(new UpdateAlternativeNameCommand({
                userId: this.userId,
                alternativeFirstName: editableAlternativeName.alternativeFirstName,
                alternativeLastName: editableAlternativeName.alternativeLastName
            }))
            : of(void 0);

        return forkJoin([saveContactInfo$, saveAlternativeName$]).pipe(
            map(() => void 0),
            catchError((errors) => {
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => errors);
            })
        );
    }

    get avatarUploadUrl(): string {
        return `/api/Employees/UploadAvatar?employeeId=${this.userId}`;
    }

    async copyToClipboard(text: string, evt: any) {
        await window.navigator.clipboard?.writeText(text);
        const tipText = 'Text copied';
        notify({
            message: tipText,
            minWidth: `${tipText.length + 2}ch`,
            width: 'auto',
            position: {of: evt.target, offset: '0 -30'}
        }, 'info', 500);
    }

    get canImpersonate(): boolean {
        return this.authService.canImpersonate(this.contactData?.id, this.contactData?.roles);
    }

    onImpersonateClick(userId: string) {
        this.authService.impersonate(userId).subscribe({
            next: () => {
                window.location.reload();
            },
            error: (errors: Array<any>) => {
                errors.forEach((errMsg => {
                    notify({
                        message: errMsg.message,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                }));
            }
        });
    }

    onChangePasswordClick() {
        this.changePasswordRequested.emit();
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    validateAlternativeFirstName = (params: {value: string}): boolean => {
        return !!params.value?.trim() || !this.alternativeName?.alternativeLastName?.trim();
    }

    validateAlternativeLastName = (params: {value: string}): boolean => {
        return !!params.value?.trim() || !this.alternativeName?.alternativeFirstName?.trim();
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserContactInformationOwn, PermissionType.UserContactInformationOthers);
    }

    get canViewAlternativeName(): boolean {
        return this.authService.canViewProfileSection(this.userId, PermissionType.UserAlternateNamesOwn, PermissionType.UserAlternateNamesOthers);
    }

    get isAlternativeNameReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserAlternateNamesOwn, PermissionType.UserAlternateNamesOthers);
    }

    protected readonly PermissionType = PermissionType;
}
